import React, { useEffect, useMemo, useState } from 'react';
import './SportTracker.css';

const API_BASE = "https://laforgeataches.fr/go/php/sport";

function minutesToHoursLabel(totalMinutes) {
  const safeMinutes = Number(totalMinutes || 0);
  const h = Math.floor(safeMinutes / 60);
  const m = safeMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

const DEFAULT_MONTHLY_GOAL_MINUTES = 900;

function getTodayDate() {
  return new Date().toISOString().slice(0, 10);
}

export default function SportTracker() {
  const [entries, setEntries] = useState([]);
  const [sportDate, setSportDate] = useState(getTodayDate());
  const [sportType, setSportType] = useState('Marche');
  const [durationMinutes, setDurationMinutes] = useState('30');
  const [goalMinutes, setGoalMinutes] = useState(DEFAULT_MONTHLY_GOAL_MINUTES);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [goalInput, setGoalInput] = useState(String(DEFAULT_MONTHLY_GOAL_MINUTES));
  const [notes, setNotes] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  async function loadEntries() {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/get-sport-entries.php`, {
        credentials: 'include',
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Erreur de chargement');
      setEntries(data.entries || []);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadSettings() {
    try {
      const response = await fetch(`${API_BASE}/get-sport-settings.php`, {
        credentials: 'include',
      });
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Erreur de chargement des paramètres sport');
      }

      const minutes = Number(data.monthly_goal_minutes || DEFAULT_MONTHLY_GOAL_MINUTES);
      setGoalMinutes(minutes);
      setGoalInput(String(minutes));
    } catch (error) {
      setMessage(error.message);
    }
  }

  async function handleSaveSettings(event) {
    event.preventDefault();
    setMessage('');

    const minutes = Number(goalInput);

    if (!Number.isFinite(minutes) || minutes <= 0) {
      return setMessage('L’objectif mensuel doit être supérieur à 0.');
    }

    try {
      const response = await fetch(`${API_BASE}/save-sport-settings.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          monthly_goal_minutes: minutes,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Erreur d’enregistrement des paramètres sport');
      }

      setGoalMinutes(minutes);
      setGoalInput(String(minutes));
      setSettingsOpen(false);
      setMessage('Objectif mensuel sport enregistré.');
    } catch (error) {
      setMessage(error.message);
    }
  }

  useEffect(() => {
    loadEntries();
    loadSettings();
  }, []);

  const selectedMonth = sportDate.slice(0, 7);
  const selectedYear = sportDate.slice(0, 4);

  const daysInMonth = new Date(
    Number(selectedYear),
    Number(selectedMonth.split("-")[1]),
    0
  ).getDate();

  const averagePerDay = Math.round(goalMinutes / daysInMonth);
  const averagePerWeek = Math.round(goalMinutes / 4.33);

  function formatFrenchDate(dateString) {
  const date = new Date(dateString);

  return date.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

  const stats = useMemo(() => {
    const monthEntries = entries.filter((entry) => entry.sport_date?.slice(0, 7) === selectedMonth);
    const yearEntries = entries.filter((entry) => entry.sport_date?.slice(0, 4) === selectedYear);

    const monthMinutes = monthEntries.reduce((sum, entry) => sum + Number(entry.duration_minutes || 0), 0);
    const yearMinutes = yearEntries.reduce((sum, entry) => sum + Number(entry.duration_minutes || 0), 0);
    const progress = goalMinutes > 0 ? Math.min(100, Math.round((monthMinutes / goalMinutes) * 100)) : 0;

    return {
      monthEntries,
      yearEntries,
      monthMinutes,
      yearMinutes,
      progress,
    };
  }, [entries, selectedMonth, selectedYear, goalMinutes]);

  async function handleSubmit(event) {
    event.preventDefault();
    setMessage('');

    const minutes = Number(durationMinutes);
    if (!sportDate) return setMessage('Choisis une date.');
    if (!sportType.trim()) return setMessage('Indique une activité.');
    if (!Number.isFinite(minutes) || minutes <= 0) return setMessage('La durée doit être supérieure à 0.');

    try {
      const response = await fetch(`${API_BASE}/save-sport-entry.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sport_date: sportDate,
          sport_type: sportType.trim(),
          duration_minutes: minutes,
          notes: notes.trim(),
        }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Erreur d’enregistrement');

      setMessage('Séance enregistrée.');
      setNotes('');
      await loadEntries();
    } catch (error) {
      setMessage(error.message);
    }
  }

  async function handleDelete(id) {
    try {
      const response = await fetch(`${API_BASE}/delete-sport-entry.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Erreur de suppression');
      await loadEntries();
    } catch (error) {
      setMessage(error.message);
    }
  }

  return (
    <section className="sport-tracker">
      <header className="sport-tracker__header">
        <div>
          <h2>Sport</h2>
          <p>Suivi simple des heures de sport</p>
        </div>

        <button
          type="button"
          className="sport-tracker__gear-btn"
          onClick={() => setSettingsOpen(true)}
          aria-label="Paramétrer l’objectif mensuel"
          title="Paramétrer l’objectif mensuel"
        >
          ⚙
        </button>
      </header>

      <div className="sport-tracker__progress-card">
        <div className="sport-tracker__progress-top">
          <strong>Objectif du mois</strong>
          <span>{stats.progress}%</span>
        </div>

        <div className="sport-tracker__progress-bar" aria-label="Progression mensuelle">
          <div
            className="sport-tracker__progress-fill"
            style={{ width: `${stats.progress}%` }}
          />
        </div>

        <div className="sport-tracker__progress-details">
          <span>{minutesToHoursLabel(stats.monthMinutes)} réalisées</span>
          <span>objectif : {minutesToHoursLabel(goalMinutes)}</span>
          <span>≈ {minutesToHoursLabel(averagePerDay)} / jour</span>
          <span>≈ {minutesToHoursLabel(averagePerWeek)} / semaine</span>
        </div>
      </div>

      {settingsOpen && (
        <div className="sport-settings-modal" role="dialog" aria-modal="true">
          <div className="sport-settings-modal__content">
            <h3>Paramètres Sport</h3>

            <form onSubmit={handleSaveSettings}>
              <label>
                Objectif mensuel
                <input
                  type="number"
                  min="1"
                  value={goalInput}
                  onChange={(event) => setGoalInput(event.target.value)}
                />
              </label>

              <p>
                Objectif actuel : <strong>{minutesToHoursLabel(goalMinutes)}</strong>
              </p>

              <div className="sport-settings-modal__actions">
                <button type="button" onClick={() => setSettingsOpen(false)}>
                  Annuler
                </button>
                <button type="submit">
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <form className="sport-tracker__form" onSubmit={handleSubmit}>
        <label>
          Date
          <input
            type="date"
            value={sportDate}
            onChange={(event) => setSportDate(event.target.value)}
          />
        </label>

        <label>
          Activité
          <input
            type="text"
            value={sportType}
            onChange={(event) => setSportType(event.target.value)}
            placeholder="Marche, vélo, piscine..."
          />
        </label>

        <label>
          Durée en minutes
          <input
            type="number"
            min="1"
            value={durationMinutes}
            onChange={(event) => setDurationMinutes(event.target.value)}
          />
        </label>

        <label className="sport-tracker__notes">
          Notes
          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Optionnel"
          />
        </label>

        <button type="submit">Enregistrer</button>
      </form>

      {message && <p className="sport-tracker__message">{message}</p>}

      <div className="sport-tracker__stats">
        <article>
          <span>Mois</span>
          <strong>{minutesToHoursLabel(stats.monthMinutes)}</strong>
        </article>
        <article>
          <span>Année</span>
          <strong>{minutesToHoursLabel(stats.yearMinutes)}</strong>
        </article>
        <article>
          <span>Séances du mois</span>
          <strong>{stats.monthEntries.length}</strong>
        </article>
      </div>

      <div className="sport-tracker__list">
        <h3>Séances du mois sélectionné</h3>
        <p className="sport-tracker__month-label">
          Mois affiché : {selectedMonth}
        </p>

        {loading && <p>Chargement...</p>}

        {!loading && stats.monthEntries.length === 0 && (
          <p>Aucune séance enregistrée pour ce mois.</p>
        )}

        {!loading && stats.monthEntries.map((entry) => (
          <div className="sport-tracker__entry" key={entry.id}>
            <div>
              <strong>{formatFrenchDate(entry.sport_date)}</strong>
              <span>{entry.sport_type} — {minutesToHoursLabel(Number(entry.duration_minutes))}</span>
              {entry.notes && <small>{entry.notes}</small>}
            </div>
            <button type="button" onClick={() => handleDelete(entry.id)}>Supprimer</button>
          </div>
        ))}
      </div>
    </section>
  );
}
